// ---------------------------------------------------------------------------
// 都道府県（030、2026-09-19 射場さん「住所の都道府県を欄にする。将来 県別の実績に使う」）。
//
// 住所の文字列（address_enc、暗号化）は今までどおり「都道府県から番地まで」の1行で持つ。
// 画面は「都道府県（選ぶ）」と「市区町村・番地」の2つの欄に分けて見せ、保存のとき1行に戻す（joinAddress）。
// 都道府県は API が住所の頭から決めて pref_code（平文・1〜47、JIS X 0401）に入れる（prefCodeOf）。
// 都道府県は個人を特定しないので平文でよい（CLAUDE.md ルール5：集計に使うものは平文）。
// ---------------------------------------------------------------------------

/** JIS X 0401 の順（北海道＝1 … 沖縄県＝47）。DB の prefectures（030）と同じ（apps/api/test/prefectures.test.ts が照らし合わせる） */
export const PREFECTURE_NAMES = [
  "北海道", "青森県", "岩手県", "宮城県", "秋田県", "山形県", "福島県",
  "茨城県", "栃木県", "群馬県", "埼玉県", "千葉県", "東京都", "神奈川県",
  "新潟県", "富山県", "石川県", "福井県", "山梨県", "長野県", "岐阜県", "静岡県", "愛知県",
  "三重県", "滋賀県", "京都府", "大阪府", "兵庫県", "奈良県", "和歌山県",
  "鳥取県", "島根県", "岡山県", "広島県", "山口県",
  "徳島県", "香川県", "愛媛県", "高知県",
  "福岡県", "佐賀県", "長崎県", "熊本県", "大分県", "宮崎県", "鹿児島県", "沖縄県",
] as const;
export type PrefectureName = (typeof PREFECTURE_NAMES)[number];

/** コードつき（「01」〜「47」の2桁。法人番号 Web-API の都道府県コードと同じ形。国税庁から引くときの絞り込みにも使う） */
export const PREFECTURES: ReadonlyArray<{ code: string; name: PrefectureName }> = PREFECTURE_NAMES.map((name, i) => ({ code: String(i + 1).padStart(2, "0"), name }));

/** 都道府県の名前 → コード（1〜47）。知らない名前は null */
export function prefCodeOfName(name: string): number | null {
  const i = PREFECTURE_NAMES.indexOf(name.trim() as PrefectureName);
  return i < 0 ? null : i + 1;
}

/** コード（1〜47）→ 名前。範囲の外は空 */
export const prefNameOf = (code: number | null | undefined): string => (code && code >= 1 && code <= 47 ? PREFECTURE_NAMES[code - 1]! : "");

/**
 * 住所の1行を「都道府県」と「残り（市区町村・番地）」に分ける。
 * 頭が都道府県の名前でなければ都道府県は空（古いデータに「豊中市…」から書いたものがある）
 */
export function splitAddress(full: string): { pref: string; rest: string } {
  const s = full.replace(/^[\s　]+/, "");
  for (const p of PREFECTURE_NAMES) {
    if (s.startsWith(p)) return { pref: p, rest: s.slice(p.length).replace(/^[\s　]+/, "") };
  }
  return { pref: "", rest: full };
}

/** 「都道府県」と「残り」を住所の1行に戻す */
export const joinAddress = (pref: string, rest: string): string => `${pref}${rest}`;

/** 住所の頭の都道府県からコード（1〜47）。頭が都道府県でなければ null。API が pref_code を決めるのに使う */
export function prefCodeOf(address: string | null | undefined): number | null {
  if (!address) return null;
  const { pref } = splitAddress(address);
  return pref ? prefCodeOfName(pref) : null;
}
